import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TradePicker } from "@/components/TradePicker";
import { Loader2, HardHat } from "lucide-react";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [picked, setPicked] = useState(user?.trades || []);
  const [busy, setBusy] = useState(false);

  // No marketing slides — post-signup users are already sold; get them into the product fast.
  const start = async () => {
    setBusy(true);
    try {
      if (picked.length) {
        try { await api.patch("/auth/profile", { trades: picked }); await refresh(); } catch { /* ignore */ }
      }
      navigate("/dashboard");
    } finally {
      setBusy(false);
    }
  };

  // Only skip onboarding for established/paid accounts — trial users still get the trial-start moment.
  const st = user?.subscription?.status;
  if (st === "active" || st === "past_due" || st === "developer" || st === "tester") {
    navigate("/dashboard");
    return null;
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background px-6" style={{ paddingTop: "calc(env(safe-area-inset-top) + 2.5rem)", paddingBottom: "calc(env(safe-area-inset-bottom) + 2.5rem)" }} data-testid="onboarding">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg">
            <HardHat className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-heading text-2xl font-bold">You're in — one quick thing</h1>
          <p className="mt-2 max-w-xs text-sm text-muted-foreground">
            What do you do? <span className="text-muted-foreground/70">(optional)</span> — we use it to sort punch lists and match vendors. Skip and do it later if you like.
          </p>
        </div>

        <div className="mt-6 max-h-[46vh] flex-1 overflow-y-auto">
          <TradePicker value={picked} onChange={setPicked} />
        </div>

        <Button
          data-testid="start-trial-btn"
          onClick={start}
          disabled={busy}
          className="mt-6 h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Start my 15-day free trial"}
        </Button>
        <button
          data-testid="onboarding-skip-btn"
          onClick={start}
          disabled={busy}
          className="mt-3 text-center text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Skip — take me to my dashboard
        </button>
        <p className="mt-4 text-center text-xs text-muted-foreground">15 days free · no credit card required</p>
      </div>
    </div>
  );
}
